import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  X,
  Upload,
  MapPin,
  Calendar,
  Heart,
  Image as ImageIcon,
  Video,
  Sparkles,
  AlertCircle,
  Compass,
  CheckCircle2,
  Loader2,
  Plus,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Layers,
  Camera,
  Shield,
  RefreshCw,
  Navigation,
} from 'lucide-react';
import { MemoryItem, AuthorRole, CoupleSettings } from '../types';
import confetti from 'canvas-confetti';
import { romanticAudio } from '../utils/audio';
import { compressBatchPhotos, isImageFile } from '../utils/imageCompressor';
import {
  detectBestDateFromPhotoBatch,
  formatReadableSpanishDate,
  PhotoDetectionResult,
} from '../utils/photoDateExtractor';
import {
  getCurrentDeviceGps,
  extractGpsFromPhotoBatch,
  DeviceGpsResult,
  PhotoGpsItem,
} from '../utils/photoLocationExtractor';
import { ConfirmationModal } from './ConfirmationModal';

interface UploadModalProps {
  isOpen: boolean;
  settings?: CoupleSettings;
  activeAdminProfile?: 'novio' | 'novia';
  onClose: () => void;
  onSaveMemory: (newMemory: Omit<MemoryItem, 'id' | 'comments' | 'createdAt'>) => Promise<void> | void;
}

const MAX_PHOTOS = 20;

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  settings,
  activeAdminProfile,
  onClose,
  onSaveMemory,
}) => {
  const [mediaType, setMediaType] = useState<'photo' | 'video'>('photo');
  const [mediaList, setMediaList] = useState<string[]>([]);
  const [activePreviewIndex, setActivePreviewIndex] = useState<number>(0);
  const [place, setPlace] = useState<string>('');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [message, setMessage] = useState<string>('');
  const [category, setCategory] = useState<MemoryItem['category']>('cita');
  const [authorRole, setAuthorRole] = useState<AuthorRole>(activeAdminProfile || 'novio');

  // Real GPS & Photo EXIF Location states
  const [locationSource, setLocationSource] = useState<'current_gps' | 'photo_exif' | 'none'>('current_gps');
  const [deviceGps, setDeviceGps] = useState<DeviceGpsResult | null>(null);
  const [deviceGpsStatus, setDeviceGpsStatus] = useState<'idle' | 'locating' | 'success' | 'error'>('idle');
  const [deviceGpsError, setDeviceGpsError] = useState<string>('');
  const [lastGpsFetchTime, setLastGpsFetchTime] = useState<number>(0);
  const [photoGpsList, setPhotoGpsList] = useState<PhotoGpsItem[]>([]);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const [customPlace, setCustomPlace] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setAuthorRole(activeAdminProfile || 'novio');
    }
  }, [isOpen, activeAdminProfile]);

  const getAuthorName = (role: AuthorRole) => {
    if (role === 'novio') return settings?.partner1Name || 'El Novio';
    if (role === 'novia') return settings?.partner2Name || 'La Novia';
    return 'Ambos';
  };
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [autoDetectedMeta, setAutoDetectedMeta] = useState<string | null>(null);
  const [detectedInfo, setDetectedInfo] = useState<PhotoDetectionResult | null>(null);
  const [uploadedRawFiles, setUploadedRawFiles] = useState<File[]>([]);
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setMediaType('photo');
    setMediaList([]);
    setActivePreviewIndex(0);
    setPlace('');
    setDate(new Date().toISOString().split('T')[0]);
    setMessage('');
    setCategory('cita');
    setIsDragging(false);
    setErrorMessage('');
    setSuccessMessage('');
    setAutoDetectedMeta(null);
    setDetectedInfo(null);
    setUploadedRawFiles([]);
    setShowPublishConfirm(false);
    setIsProcessing(false);
    setIsSubmitting(false);
    setLocationSource('current_gps');
    setDeviceGps(null);
    setDeviceGpsStatus('idle');
    setDeviceGpsError('');
    setLastGpsFetchTime(0);
    setPhotoGpsList([]);
    setSelectedPhotoIndex(0);
    setCustomPlace('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  // 1. Obtains real GPS location via browser Geolocation API with high accuracy
  const requestCurrentGps = async () => {
    setDeviceGpsStatus('locating');
    setDeviceGpsError('');
    try {
      const result = await getCurrentDeviceGps();
      setDeviceGps(result);
      setDeviceGpsStatus('success');
      setLastGpsFetchTime(Date.now());
      if (locationSource === 'current_gps' || !place) {
        setPlace(result.placeName);
      }
    } catch (err: any) {
      setDeviceGpsStatus('error');
      setDeviceGpsError(err?.message || 'No se pudo obtener la ubicación GPS.');
    }
  };

  // Automatic GPS fetch as soon as user opens "Nuevo Recuerdo de Pareja"
  useEffect(() => {
    if (isOpen) {
      requestCurrentGps();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
    }
  };

  const processFiles = async (files: File[]) => {
    setErrorMessage('');
    const validFiles = files.filter((f) => f.type.startsWith('image/') || f.type.startsWith('video/') || isImageFile(f));

    if (validFiles.length === 0) {
      setErrorMessage('Por favor selecciona imágenes o videos válidos.');
      return;
    }

    const availableSlots = MAX_PHOTOS - mediaList.length;
    if (availableSlots <= 0) {
      setErrorMessage(`Has alcanzado el límite máximo de ${MAX_PHOTOS} fotos por publicación.`);
      return;
    }

    const filesToProcess = validFiles.slice(0, availableSlots);
    if (validFiles.length > availableSlots) {
      setErrorMessage(`Solo se pueden subir hasta ${MAX_PHOTOS} fotos por publicación. Se agregaron las primeras ${availableSlots}.`);
    }

    setIsProcessing(true);
    setUploadedRawFiles((prev) => [...prev, ...filesToProcess]);

    // 1. Automatically detect original capture date from photo metadata
    try {
      const detected = await detectBestDateFromPhotoBatch(filesToProcess);
      if (detected) {
        setDate(detected.date);
        setDetectedInfo(detected);
        setAutoDetectedMeta(`${detected.description} (${formatReadableSpanishDate(detected.date)})`);
      }
    } catch (dateErr) {
      console.log('Photo date detection error:', dateErr);
    }

    // 2. Automatically extract EXIF GPS tags from selected photographs
    try {
      const gpsItems = await extractGpsFromPhotoBatch(filesToProcess);
      setPhotoGpsList(gpsItems);
      const firstWithGps = gpsItems.find((p) => p.hasGps);
      if (firstWithGps) {
        setSelectedPhotoIndex(firstWithGps.photoIndex);
        // If device GPS is not available or errored, offer photo location automatically
        if (deviceGpsStatus !== 'success') {
          setLocationSource('photo_exif');
          setPlace(firstWithGps.placeName || '');
        }
      }
    } catch (gpsErr) {
      console.warn('Notice reading EXIF GPS from photos:', gpsErr);
    }

    // Read and compress image files, handle short video clips safely
    // Accepts ANY image format: HEIC, HEIF, WebP, AVIF, JPEG, PNG, GIF, BMP, TIFF, SVG, etc.
    const imageFiles = filesToProcess.filter((f) => isImageFile(f) || !f.type.startsWith('video/'));
    const videoFiles = filesToProcess.filter((f) => f.type.startsWith('video/'));

    // Check video size limit (< 900KB)
    const oversizedVideos = videoFiles.filter((f) => f.size > 900 * 1024);
    if (oversizedVideos.length > 0) {
      setErrorMessage(
        'Los videos deben ser clips muy cortos (< 900 KB) para guardarse rápidamente en la nube. Te sugerimos subir fotos nítidas para este momento.'
      );
    }
    const validVideos = videoFiles.filter((f) => f.size <= 900 * 1024);

    try {
      const compressedImages = await compressBatchPhotos(imageFiles);

      const videoPromises = validVideos.map((file) => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('Error al leer video'));
          reader.readAsDataURL(file);
        });
      });

      const videoResults = await Promise.all(videoPromises);
      const updatedList = [...mediaList, ...compressedImages, ...videoResults];
      setMediaList(updatedList);

      if (validVideos.length > 0 && updatedList.length === validVideos.length) {
        setMediaType('video');
      }
    } catch (err) {
      console.error('Media processing error:', err);
      setErrorMessage('Ocurrió un error al procesar las imágenes. Por favor intenta de nuevo.');
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (indexToRemove: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = mediaList.filter((_, idx) => idx !== indexToRemove);
    setMediaList(updated);
    if (activePreviewIndex >= updated.length) {
      setActivePreviewIndex(Math.max(0, updated.length - 1));
    }
  };

  // Re-scan photo date manually if requested
  const handleReScanDate = async () => {
    if (uploadedRawFiles.length === 0) return;
    setIsProcessing(true);
    try {
      const detected = await detectBestDateFromPhotoBatch(uploadedRawFiles);
      if (detected) {
        setDate(detected.date);
        setDetectedInfo(detected);
        setAutoDetectedMeta(`${detected.description} (${formatReadableSpanishDate(detected.date)})`);
      } else {
        setErrorMessage('No se encontró información de fecha en los metadatos de las fotos.');
      }
    } catch {
      setErrorMessage('No fue posible leer la fecha de las fotos.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mediaList.length === 0) {
      setErrorMessage('Debes seleccionar o subir al menos una foto o video.');
      return;
    }
    if (!date) {
      setErrorMessage('Por favor indica la fecha del recuerdo.');
      return;
    }
    if (!message.trim()) {
      setErrorMessage('Por favor escribe un mensaje de amor debajo de la foto.');
      return;
    }

    // Input fields are valid! Open confirmation notice before saving
    setErrorMessage('');
    setShowPublishConfirm(true);
  };

  // Confirmed execution of publish
  const executePublish = async () => {
    setShowPublishConfirm(false);
    try {
      setIsSubmitting(true);
      setErrorMessage('');

      let activeDeviceGps = deviceGps;
      // If user remained on the form for more than 3 minutes, refresh GPS coordinates to avoid stale coordinates
      if (locationSource === 'current_gps') {
        const elapsedMinutes = (Date.now() - lastGpsFetchTime) / (1000 * 60);
        if (elapsedMinutes > 3) {
          try {
            activeDeviceGps = await getCurrentDeviceGps();
            setDeviceGps(activeDeviceGps);
            setLastGpsFetchTime(Date.now());
          } catch {
            // Keep existing reading if refresh times out
          }
        }
      }

      const authorName = getAuthorName(authorRole);
      const memoryPayload: any = {
        type: mediaType,
        mediaUrl: mediaList[0],
        mediaUrls: mediaList,
        date,
        message: message.trim(),
        category,
        authorRole,
        authorName,
        authorAvatar:
          authorRole === 'novio'
            ? settings?.partner1Avatar
            : authorRole === 'novia'
            ? settings?.partner2Avatar
            : undefined,
      };

      // Set explicit location fields according to user priority selection
      if (locationSource === 'current_gps' && activeDeviceGps) {
        memoryPayload.locationSource = 'current_gps';
        memoryPayload.latitude = activeDeviceGps.latitude;
        memoryPayload.longitude = activeDeviceGps.longitude;
        memoryPayload.locationName = activeDeviceGps.placeName;
        memoryPayload.accuracyMeters = activeDeviceGps.accuracyMeters;
        memoryPayload.locationCapturedAt = activeDeviceGps.capturedAt;
        memoryPayload.place = customPlace.trim() || activeDeviceGps.placeName || 'Lugar especial';
      } else if (locationSource === 'photo_exif') {
        const photoGps =
          photoGpsList.find((p) => p.photoIndex === selectedPhotoIndex) ||
          photoGpsList.find((p) => p.hasGps);
        if (
          photoGps &&
          photoGps.hasGps &&
          typeof photoGps.latitude === 'number' &&
          typeof photoGps.longitude === 'number'
        ) {
          memoryPayload.locationSource = 'photo_exif';
          memoryPayload.latitude = photoGps.latitude;
          memoryPayload.longitude = photoGps.longitude;
          memoryPayload.locationName = photoGps.placeName;
          memoryPayload.locationPhotoIndex = photoGps.photoIndex;
          memoryPayload.locationCapturedAt = new Date().toISOString();
          memoryPayload.place = customPlace.trim() || photoGps.placeName || 'Lugar especial';
        } else {
          memoryPayload.locationSource = 'none';
          memoryPayload.place = customPlace.trim() || place.trim() || 'Lugar especial';
        }
      } else {
        memoryPayload.locationSource = 'none';
        memoryPayload.place = customPlace.trim() || place.trim() || 'Lugar especial';
      }

      await onSaveMemory(memoryPayload);

      setSuccessMessage('¡Recuerdo publicado con éxito en vuestro álbum! 🧡');
      romanticAudio.playChime();
      confetti({
        particleCount: 50,
        spread: 85,
        origin: { y: 0.6 },
        colors: ['#FBC02D', '#EC407A', '#AB47BC', '#42A5F5'],
      });

      setTimeout(() => {
        setIsSubmitting(false);
        setSuccessMessage('');
        resetForm();
        onClose();
      }, 700);
    } catch (err: any) {
      console.error('Error saving memory:', err);
      setIsSubmitting(false);
      setErrorMessage(
        err?.message || 'Ocurrió un problema al publicar el recuerdo. Por favor revisa tu conexión e inténtalo de nuevo.'
      );
    }
  };

  const activeMediaUrl = mediaList[activePreviewIndex] || mediaList[0];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/35 backdrop-blur-md overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white text-[#302B30] rounded-3xl shadow-2xl border border-[#F0DDE4] max-w-xl w-full overflow-hidden my-6 max-h-[92vh] flex flex-col"
        >
          {/* MODAL HEADER */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-50/60 via-rose-50/40 to-white shrink-0">
            <div className="flex items-center gap-2.5">
              <span className="text-2xl">📸</span>
              <div>
                <h3 className="font-['Playfair_Display',serif] text-xl font-semibold text-slate-800">
                  Nuevo Recuerdo de Pareja
                </h3>
                <p className="text-xs text-slate-500">
                  Puedes subir hasta <strong className="text-amber-700">20 fotos</strong> en un carrusel por publicación
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors cursor-pointer disabled:opacity-40"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* FORM BODY */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
            {successMessage && (
              <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2.5 text-xs text-emerald-800 font-semibold animate-fadeIn shadow-xs">
                <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                <span>{successMessage}</span>
              </div>
            )}

            {errorMessage && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-2 text-xs text-rose-700">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* MEDIA UPLOAD AREA */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-semibold text-slate-700">
                  Fotos o Videos del Momento * ({mediaList.length}/{MAX_PHOTOS})
                </label>
                {mediaList.length > 0 && (
                  <span className="text-[11px] font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                    {mediaList.length} de {MAX_PHOTOS} fotos
                  </span>
                )}
              </div>

              {mediaList.length > 0 ? (
                <div className="space-y-2.5">
                  {/* Hero Preview of Selected Photo with Navigation */}
                  <div className="relative min-h-[260px] max-h-[460px] aspect-[4/3] sm:aspect-square rounded-2xl overflow-hidden bg-slate-950 border border-slate-200 group shadow-inner flex items-center justify-center">
                    {/* Ambient Blurred Backdrop so there are no black borders */}
                    {mediaType !== 'video' && typeof activeMediaUrl === 'string' && !activeMediaUrl.startsWith('data:video') && (
                      <div
                        className="absolute inset-0 bg-cover bg-center blur-xl opacity-60 scale-125 pointer-events-none"
                        style={{ backgroundImage: `url(${activeMediaUrl})` }}
                      />
                    )}
                    {mediaType === 'video' || (typeof activeMediaUrl === 'string' && activeMediaUrl.startsWith('data:video')) ? (
                      <video src={activeMediaUrl} controls className="relative z-10 w-full h-full object-contain" />
                    ) : (
                      <img src={activeMediaUrl} alt={`Preview ${activePreviewIndex + 1}`} className="relative z-10 w-full h-full object-contain" />
                    )}

                    {/* Badge */}
                    <div className="absolute top-2 left-2 px-2.5 py-1 bg-black/60 backdrop-blur-md rounded-full text-white text-[11px] font-semibold border border-white/10 flex items-center gap-1">
                      <ImageIcon className="w-3 h-3 text-amber-300" />
                      <span>Foto {activePreviewIndex + 1} de {mediaList.length}</span>
                    </div>

                    {/* Navigation inside Preview */}
                    {mediaList.length > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={() => setActivePreviewIndex((prev) => (prev > 0 ? prev - 1 : mediaList.length - 1))}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/90 text-white rounded-full transition-all cursor-pointer"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setActivePreviewIndex((prev) => (prev < mediaList.length - 1 ? prev + 1 : 0))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 hover:bg-black/90 text-white rounded-full transition-all cursor-pointer"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </>
                    )}

                    {/* Delete Active Button */}
                    <button
                      type="button"
                      onClick={(e) => handleRemovePhoto(activePreviewIndex, e)}
                      className="absolute top-2 right-2 p-1.5 bg-rose-600/90 hover:bg-rose-700 text-white rounded-full transition-colors cursor-pointer shadow-md"
                      title="Eliminar esta foto"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Thumbnail Strip / Grid with Add More Button */}
                  <div className="flex items-center gap-2 overflow-x-auto p-1.5 bg-slate-50 border border-slate-200 rounded-xl no-scrollbar">
                    {mediaList.map((url, idx) => (
                      <div
                        key={idx}
                        onClick={() => setActivePreviewIndex(idx)}
                        className={`relative w-14 h-14 rounded-lg overflow-hidden shrink-0 cursor-pointer border-2 transition-all group/thumb ${
                          idx === activePreviewIndex
                            ? 'border-amber-500 scale-105 shadow-sm'
                            : 'border-slate-200 opacity-75 hover:opacity-100'
                        }`}
                      >
                        <img src={url} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                        <span className="absolute bottom-0.5 right-0.5 text-[9px] bg-black/70 text-white px-1 rounded font-bold">
                          {idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => handleRemovePhoto(idx, e)}
                          className="absolute top-0.5 right-0.5 w-4 h-4 bg-rose-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}

                    {/* Add More Photos Button (up to 100) */}
                    {mediaList.length < MAX_PHOTOS && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-14 h-14 rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-800 flex flex-col items-center justify-center shrink-0 transition-all cursor-pointer"
                        title="Agregar más fotos a esta publicación"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-[9px] font-bold">+ Fotos</span>
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all flex flex-col items-center justify-center ${
                    isDragging
                      ? 'border-amber-500 bg-amber-50/50'
                      : 'border-slate-300 hover:border-amber-400 bg-slate-50/50'
                  }`}
                >
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 mb-2">
                    <Upload className="w-5 h-5" />
                  </div>
                  <p className="text-xs font-semibold text-slate-700">
                    {isProcessing ? 'Optimizando fotos...' : 'Arrastra tus fotos aquí o haz clic (máx. 20 fotos)'}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Admite cualquier formato: JPG, PNG, HEIC (iPhone), WebP, AVIF, etc.
                  </p>
                </div>
              )}

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,video/*,.heic,.heif,.HEIC,.HEIF,.webp,.avif,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.tif,.svg,.dng,.raw"
                multiple
                className="hidden"
              />
            </div>

            {/* AUTO-DETECTED BANNER IF AVAILABLE */}
            {detectedInfo && (
              <div className="p-3 bg-gradient-to-r from-amber-500/10 via-rose-500/10 to-amber-500/10 border border-amber-300/70 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-xs text-amber-950 animate-fadeIn shadow-xs">
                <div className="flex items-start sm:items-center gap-2.5">
                  <div className="p-1.5 rounded-full bg-amber-400 text-slate-950 shrink-0 shadow-xs">
                    <Sparkles className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-900">
                        Fecha detectada de la foto:
                      </span>
                      <span className="font-extrabold text-amber-900 bg-amber-100/90 border border-amber-300/60 px-2 py-0.5 rounded-md">
                        {formatReadableSpanishDate(detectedInfo.date)}
                        {detectedInfo.time ? ` (${detectedInfo.time})` : ''}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-600 mt-0.5">
                      {detectedInfo.description}
                      {detectedInfo.matchedFileName ? ` • Archivo: "${detectedInfo.matchedFileName}"` : ''}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setDate(new Date().toISOString().split('T')[0]);
                      setDetectedInfo(null);
                      setAutoDetectedMeta(null);
                    }}
                    className="px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:text-slate-900 bg-white/90 hover:bg-white border border-slate-200 rounded-lg transition-colors cursor-pointer shadow-2xs"
                  >
                    Usar hoy
                  </button>
                </div>
              </div>
            )}

            {/* SECCIÓN: LUGAR DEL RECUERDO (GPS REAL & FOTO EXIF AUTOMÁTICOS) */}
            <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-3.5 sm:p-4 space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-rose-500" />
                  <span>Lugar del Recuerdo</span>
                </label>
                <button
                  type="button"
                  onClick={requestCurrentGps}
                  disabled={deviceGpsStatus === 'locating'}
                  className="text-[11px] text-rose-600 hover:text-rose-700 flex items-center gap-1 font-semibold transition-colors cursor-pointer disabled:opacity-50"
                  title="Actualizar señal GPS del dispositivo"
                >
                  <RefreshCw className={`w-3 h-3 ${deviceGpsStatus === 'locating' ? 'animate-spin' : ''}`} />
                  <span>Actualizar GPS</span>
                </button>
              </div>

              {/* SELECTOR DE FUENTE DE UBICACIÓN (PRIORIDAD) */}
              <div className="grid grid-cols-3 gap-1.5 bg-slate-200/60 p-1 rounded-xl text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => {
                    setLocationSource('current_gps');
                    if (deviceGps) setPlace(customPlace || deviceGps.placeName);
                    else requestCurrentGps();
                  }}
                  className={`py-1.5 px-2 rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                    locationSource === 'current_gps'
                      ? 'bg-white text-rose-700 shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Navigation className="w-3 h-3" />
                  <span className="truncate">GPS actual</span>
                  {deviceGpsStatus === 'success' && (
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setLocationSource('photo_exif');
                    const firstWithGps = photoGpsList.find((p) => p.hasGps);
                    if (firstWithGps) setPlace(customPlace || firstWithGps.placeName || '');
                  }}
                  className={`py-1.5 px-2 rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                    locationSource === 'photo_exif'
                      ? 'bg-white text-amber-700 shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Camera className="w-3 h-3" />
                  <span className="truncate">
                    Foto {photoGpsList.filter((p) => p.hasGps).length > 0 ? `(${photoGpsList.filter((p) => p.hasGps).length})` : ''}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setLocationSource('none');
                    setPlace(customPlace || '');
                  }}
                  className={`py-1.5 px-2 rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                    locationSource === 'none'
                      ? 'bg-white text-slate-800 shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Shield className="w-3 h-3" />
                  <span className="truncate">Sin ubicación</span>
                </button>
              </div>

              {/* DETALLE Y ESTADO SEGÚN LA FUENTE SELECCIONADA */}
              {locationSource === 'current_gps' && (
                <div className="bg-white border border-rose-200/80 rounded-xl p-2.5 space-y-2">
                  {deviceGpsStatus === 'locating' ? (
                    <div className="flex items-center gap-2 text-xs text-rose-700 py-1">
                      <Loader2 className="w-4 h-4 animate-spin text-rose-500" />
                      <span className="font-medium">Obteniendo ubicación GPS precisa del dispositivo...</span>
                    </div>
                  ) : deviceGpsStatus === 'success' && deviceGps ? (
                    <div>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 text-xs text-emerald-800 font-bold">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span>Ubicación obtenida:</span>
                          <span className="text-slate-900 underline decoration-emerald-400 font-extrabold">{deviceGps.placeName}</span>
                        </div>
                        {deviceGps.accuracyMeters > 0 && (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                            Precisión: ±{deviceGps.accuracyMeters} m
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-500 mt-1">
                        Coordenadas: {deviceGps.latitude.toFixed(4)}°, {deviceGps.longitude.toFixed(4)}° • GPS en tiempo real
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-1.5 text-xs text-amber-800 font-semibold">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span>No se pudo obtener la ubicación GPS</span>
                      </div>
                      <p className="text-[11px] text-slate-500">
                        {deviceGpsError || 'Permiso de ubicación no concedido o señal no disponible.'}
                      </p>
                      <button
                        type="button"
                        onClick={requestCurrentGps}
                        className="text-[11px] text-rose-600 font-semibold underline cursor-pointer"
                      >
                        Reintentar conexión GPS
                      </button>
                    </div>
                  )}
                </div>
              )}

              {locationSource === 'photo_exif' && (
                <div className="bg-white border border-amber-200/80 rounded-xl p-2.5 space-y-2">
                  {photoGpsList.filter((p) => p.hasGps).length > 0 ? (
                    <div>
                      <div className="flex items-center gap-1.5 text-xs text-amber-900 font-bold mb-1.5">
                        <Camera className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span>Ubicación original detectada en la fotografía</span>
                      </div>

                      {photoGpsList.filter((p) => p.hasGps).length > 1 && (
                        <div className="mb-2">
                          <label className="block text-[10px] font-semibold text-slate-600 mb-1">
                            Selecciona la foto cuya ubicación deseas usar:
                          </label>
                          <select
                            value={selectedPhotoIndex}
                            onChange={(e) => {
                              const idx = Number(e.target.value);
                              setSelectedPhotoIndex(idx);
                              const match = photoGpsList.find((p) => p.photoIndex === idx);
                              if (match) setPlace(customPlace || match.placeName || '');
                            }}
                            className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg p-1.5 text-slate-800"
                          >
                            {photoGpsList
                              .filter((p) => p.hasGps)
                              .map((p) => (
                                <option key={p.photoIndex} value={p.photoIndex}>
                                  Foto #{p.photoIndex + 1} ({p.fileName}): {p.placeName}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                      {(() => {
                        const activePhotoGps =
                          photoGpsList.find((p) => p.photoIndex === selectedPhotoIndex) ||
                          photoGpsList.find((p) => p.hasGps);
                        return activePhotoGps ? (
                          <div className="text-xs">
                            <span className="font-semibold text-slate-700">Lugar de la foto: </span>
                            <span className="font-extrabold text-amber-900">{activePhotoGps.placeName}</span>
                            {activePhotoGps.latitude && (
                              <p className="text-[10px] text-slate-500 mt-0.5">
                                Coordenadas EXIF: {activePhotoGps.latitude.toFixed(4)}°, {activePhotoGps.longitude?.toFixed(4)}°
                              </p>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-600 space-y-1">
                      <div className="flex items-center gap-1.5 text-slate-700 font-semibold">
                        <AlertCircle className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span>Esta foto no contiene información de ubicación</span>
                      </div>
                      <p className="text-[11px] text-slate-500">
                        Los metadatos EXIF no contienen etiquetas GPS. Puedes cambiar a &ldquo;GPS actual&rdquo; o &ldquo;Sin ubicación&rdquo;.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {locationSource === 'none' && (
                <div className="bg-white border border-slate-200 rounded-xl p-2.5 text-xs text-slate-600 flex items-start gap-2">
                  <Shield className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold text-slate-800">Publicando sin coordenadas</span>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Tu privacidad está protegida. No se guardará ninguna coordenada GPS con este recuerdo.
                    </p>
                  </div>
                </div>
              )}

              {/* CAMPO DE PERSONALIZACIÓN DEL NOMBRE DEL LUGAR (OPCIONAL) */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                  Nombre legible del lugar (opcional para personalizar)
                </label>
                <div className="relative">
                  <MapPin className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder={
                      locationSource === 'current_gps' && deviceGps?.placeName
                        ? deviceGps.placeName
                        : locationSource === 'photo_exif' && photoGpsList.find((p) => p.hasGps)?.placeName
                        ? photoGpsList.find((p) => p.hasGps)?.placeName
                        : 'Ej. Playa Blanca, Mirador, o déjalo automático'
                    }
                    value={customPlace}
                    onChange={(e) => {
                      setCustomPlace(e.target.value);
                      setPlace(e.target.value);
                    }}
                    className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-400 text-slate-900 placeholder:text-slate-400 font-medium"
                  />
                </div>
              </div>

              <p className="text-[10px] text-slate-500 flex items-center gap-1">
                <span>💡 Compartir la ubicación puede revelar dónde se tomó la foto o dónde te encontrabas al publicar.</span>
              </p>
            </div>

            {/* FECHA */}
            <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-700">
                    Fecha del Recuerdo *
                  </label>
                  {detectedInfo ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-full shadow-2xs">
                      <Sparkles className="w-2.5 h-2.5 text-emerald-600" />
                      <span>Detectada automáticamente</span>
                    </span>
                  ) : uploadedRawFiles.length > 0 ? (
                    <button
                      type="button"
                      onClick={handleReScanDate}
                      className="text-[10px] text-amber-700 hover:text-amber-800 flex items-center gap-1 font-semibold transition-colors cursor-pointer"
                      title="Volver a escanear fotos subidas"
                    >
                      <Sparkles className="w-2.5 h-2.5 text-amber-500" />
                      <span>Re-escanear foto</span>
                    </button>
                  ) : null}
                </div>
                <div className="relative">
                  <Calendar className="w-4 h-4 text-amber-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="date"
                    required
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className={`w-full pl-9 pr-3 py-2 text-xs sm:text-sm bg-slate-50 border rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 text-slate-900 [color-scheme:light] font-medium transition-all ${
                      detectedInfo ? 'border-amber-300 ring-1 ring-amber-200/60 bg-amber-50/20' : 'border-slate-200'
                    }`}
                  />
                </div>
                {detectedInfo ? (
                  <div className="mt-1 flex items-center justify-between text-[11px] text-slate-600 px-1">
                    <span className="font-semibold text-amber-900">
                      📅 {formatReadableSpanishDate(date)}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setDate(new Date().toISOString().split('T')[0]);
                        setDetectedInfo(null);
                        setAutoDetectedMeta(null);
                      }}
                      className="text-[10px] text-slate-500 hover:text-rose-600 underline cursor-pointer"
                    >
                      Poner hoy
                    </button>
                  </div>
                ) : (
                  <span className="block text-[10px] text-slate-600 mt-1 pl-1">
                    Coloca automáticamente la fecha exacta al subir tu foto
                  </span>
                )}
              </div>

            {/* MENSAJE DEBAJO DE LA FOTO */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Mensaje Debajo de la Foto *
              </label>
              <textarea
                required
                rows={3}
                placeholder="Escribe lo que sentiste ese día, una frase romántica o un recuerdo inolvidable..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full p-3 text-xs sm:text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 text-slate-900 placeholder:text-slate-400 font-medium"
              />
            </div>

            {/* QUIÉN PUBLICA ESTE RECUERDO */}
            <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-3 sm:p-3.5">
              <label className="block text-xs font-semibold text-slate-800 mb-2 flex items-center justify-between">
                <span>¿Quién publica este recuerdo? *</span>
                <span className="text-[11px] text-slate-500 font-normal">Se mostrará en la publicación</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setAuthorRole('novio')}
                  className={`p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1 transition-all cursor-pointer ${
                    authorRole === 'novio'
                      ? 'bg-sky-50 border-sky-400 text-sky-900 shadow-sm ring-2 ring-sky-300/50'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100/80'
                  }`}
                >
                  <span className="text-base">💙</span>
                  <span className="truncate w-full text-center font-bold">
                    {settings?.partner1Name || 'El Novio'}
                  </span>
                  <span className="text-[10px] text-sky-600 font-medium">Novio</span>
                </button>

                <button
                  type="button"
                  onClick={() => setAuthorRole('novia')}
                  className={`p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1 transition-all cursor-pointer ${
                    authorRole === 'novia'
                      ? 'bg-rose-50 border-rose-400 text-rose-900 shadow-sm ring-2 ring-rose-300/50'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100/80'
                  }`}
                >
                  <span className="text-base">💖</span>
                  <span className="truncate w-full text-center font-bold">
                    {settings?.partner2Name || 'La Novia'}
                  </span>
                  <span className="text-[10px] text-rose-600 font-medium">Novia</span>
                </button>

                <button
                  type="button"
                  onClick={() => setAuthorRole('ambos')}
                  className={`p-2.5 rounded-xl border text-xs font-semibold flex flex-col items-center gap-1 transition-all cursor-pointer ${
                    authorRole === 'ambos'
                      ? 'bg-amber-50 border-amber-400 text-amber-950 shadow-sm ring-2 ring-amber-300/50'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-100/80'
                  }`}
                >
                  <span className="text-base">💑</span>
                  <span className="truncate w-full text-center font-bold">Ambos</span>
                  <span className="text-[10px] text-amber-700 font-medium">En pareja</span>
                </button>
              </div>
            </div>

            {/* CATEGORY */}
            <div className="pt-1">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Categoría
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as MemoryItem['category'])}
                  className="w-full p-2.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-amber-400 text-slate-900 font-medium [color-scheme:light] cursor-pointer"
                >
                  <option value="cita">Cita Romántica ☕</option>
                  <option value="viaje">Viaje Juntos ✈️</option>
                  <option value="aniversario">Aniversario 💍</option>
                  <option value="cotidiano">Día a Día 🌿</option>
                  <option value="especial">Momento Especial ✨</option>
                </select>
              </div>
            </div>

            {/* SUBMIT BUTTON */}
            <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer disabled:opacity-40"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={mediaList.length === 0 || isProcessing || isSubmitting}
                className="px-6 py-2.5 bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 disabled:opacity-50 text-slate-900 font-semibold text-xs sm:text-sm rounded-xl shadow-md transition-all hover:scale-105 active:scale-95 cursor-pointer disabled:cursor-not-allowed flex items-center gap-2"
                id="btn-submit-memory"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-slate-900" />
                    <span>Publicando recuerdo...</span>
                  </>
                ) : isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin text-slate-900" />
                    <span>Optimizando fotos...</span>
                  </>
                ) : (
                  <span>Publicar en Nuestro Álbum 🧡</span>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      {/* AVISO DE CONFIRMACIÓN AL PUBLICAR */}
      <ConfirmationModal
        isOpen={showPublishConfirm}
        onClose={() => setShowPublishConfirm(false)}
        onConfirm={executePublish}
        type="publish"
        title="¿Confirmas publicar este recuerdo?"
        description="Tu recuerdo se guardará en vuestro álbum compartido para que puedan revivirlo juntos cuando quieran."
        confirmText="Sí, publicar recuerdo 🧡"
        cancelText="Revisar datos"
        isLoading={isSubmitting}
        previewImage={mediaList[0]}
        previewDetails={{
          place: place.trim(),
          date: formatReadableSpanishDate(date) || date,
          message: message.trim(),
          mediaCount: mediaList.length,
          author: `Publicado por: ${getAuthorName(authorRole)} (${
            authorRole === 'novio' ? 'Novio 💙' : authorRole === 'novia' ? 'Novia 💖' : 'Ambos 💑'
          })`,
        }}
      />
    </AnimatePresence>
  );
};
